import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Save, Clock, Phone } from "lucide-react";

export default function SiteSettings() {
  const [site, setSite] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSite({
      church_name: r.data.church_name || "", tagline: r.data.tagline || "", welcome: r.data.welcome || "",
      service_times: r.data.service_times || [], contact: r.data.contact || { address: "", phone: "", email: "" },
    })).catch(() => {});
  }, []);

  if (!site) return <div className="h-40 rounded-xl bg-[#F3EFEA] animate-pulse" />;

  const setService = (i, field, val) => {
    const next = [...site.service_times];
    next[i] = { ...next[i], [field]: val };
    setSite({ ...site, service_times: next });
  };
  const addService = () => setSite({ ...site, service_times: [...site.service_times, { day: "", name: "", time: "" }] });
  const removeService = (i) => setSite({ ...site, service_times: site.service_times.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", site);
      toast.success("Site content updated — your public page now reflects the changes");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="site-settings-page">
      <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm space-y-4">
        <h3 className="font-serif text-xl font-semibold text-[#1C241B]">Church Identity</h3>
        <div className="space-y-1.5"><Label>Church name</Label>
          <Input data-testid="settings-church-name" value={site.church_name} onChange={(e) => setSite({ ...site, church_name: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Tagline</Label>
          <Input data-testid="settings-tagline" value={site.tagline} onChange={(e) => setSite({ ...site, tagline: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Welcome message</Label>
          <Textarea rows={4} data-testid="settings-welcome" value={site.welcome} onChange={(e) => setSite({ ...site, welcome: e.target.value })} /></div>
      </Card>

      <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl font-semibold text-[#1C241B] flex items-center gap-2"><Clock className="h-5 w-5 text-[#2D5A27]" /> Service Times</h3>
          <Button size="sm" variant="outline" onClick={addService} data-testid="add-service-button" className="border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </div>
        <div className="space-y-3">
          {site.service_times.map((s, i) => (
            <div key={i} className="grid grid-cols-[1fr_1.5fr_1.2fr_auto] gap-2 items-center" data-testid={`service-row-${i}`}>
              <Input placeholder="Day" value={s.day} onChange={(e) => setService(i, "day", e.target.value)} data-testid={`service-day-${i}`} />
              <Input placeholder="Service name" value={s.name} onChange={(e) => setService(i, "name", e.target.value)} data-testid={`service-name-${i}`} />
              <Input placeholder="Time" value={s.time} onChange={(e) => setService(i, "time", e.target.value)} data-testid={`service-time-${i}`} />
              <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => removeService(i)} data-testid={`remove-service-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          {site.service_times.length === 0 && <p className="text-sm text-[#7A8A78]">No service times. Add one above.</p>}
        </div>
      </Card>

      <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm space-y-4">
        <h3 className="font-serif text-xl font-semibold text-[#1C241B] flex items-center gap-2"><Phone className="h-5 w-5 text-[#2D5A27]" /> Contact Details</h3>
        <div className="space-y-1.5"><Label>Address</Label>
          <Input data-testid="settings-address" value={site.contact.address} onChange={(e) => setSite({ ...site, contact: { ...site.contact, address: e.target.value } })} /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Phone</Label>
            <Input data-testid="settings-phone" value={site.contact.phone} onChange={(e) => setSite({ ...site, contact: { ...site.contact, phone: e.target.value } })} /></div>
          <div className="space-y-1.5"><Label>Email</Label>
            <Input data-testid="settings-email" value={site.contact.email} onChange={(e) => setSite({ ...site, contact: { ...site.contact, email: e.target.value } })} /></div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-settings-button" className="h-11 bg-[#2D5A27] hover:bg-[#23471F] text-white">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}
